import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { User, Building, Briefcase, Upload, X, Image, Video, ArrowLeft } from 'lucide-react';
import { useChatSocket } from '../../hooks/useChatSocket';
import { useNavigate } from 'react-router-dom';

interface Persona {
  id: number;
  name: string;
  [key: string]: any; // for extra details
}

interface Message {
  role: string;
  content: string;
  images?: string[];
  videos?: string[];
  // Optionally, add timestamp, personaId, etc.
}

interface ChatUIProps {
  personaIds: number[];
}

const API_URL = import.meta.env.VITE_API_URL || '';

// Helper function to recursively find a 'name' key in a nested object
const findName = (obj: any): string | undefined => {
  if (!obj || typeof obj !== 'object') return undefined;
  
  // Check top-level first
  if (typeof obj.name === 'string' && obj.name.trim()) return obj.name;
  
  // Search nested objects
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (typeof value === 'object' && value !== null) {
      const found = findName(value);
      if (found) return found;
    }
  }
  
  return undefined;
};

// Generic recursive function to render persona data
const renderPersonaData = (data: any, title: string, bgColor: string, borderColor: string, textColor: string) => {
  if (!data || (Array.isArray(data) && data.length === 0)) return null;

  const renderItem = (item: any, index: number) => {
    if (typeof item === 'string') {
      return <li key={index} className="text-sm text-gray-700">{item}</li>;
    } else if (typeof item === 'object' && item !== null) {
      return (
        <li key={index} className="text-sm text-gray-700 mb-2">
          {Object.entries(item).map(([key, value]) => (
            <div key={key} className="ml-4 mb-1">
              <span className="font-medium text-gray-600 capitalize">{key.replace(/_/g, ' ')}: </span>
              {typeof value === 'string' ? (
                <span>{value}</span>
              ) : Array.isArray(value) ? (
                <ul className="list-disc ml-4 mt-1">
                  {value.map((subItem, subIndex) => renderItem(subItem, subIndex))}
                </ul>
              ) : typeof value === 'object' && value !== null ? (
                <div className="ml-4">
                  {Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey} className="mb-1">
                      <span className="font-medium text-gray-500 text-xs capitalize">{subKey.replace(/_/g, ' ')}: </span>
                      <span className="text-xs">{String(subValue)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span>{String(value)}</span>
              )}
            </div>
          ))}
        </li>
      );
    } else {
      return <li key={index} className="text-sm text-gray-700">{String(item)}</li>;
    }
  };

  return (
    <div className={`${bgColor} p-5 rounded-lg border ${borderColor}`}>
      <h4 className={`font-medium ${textColor} mb-3 flex items-center`}>
        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
        {title}
      </h4>
      <ul className="list-disc ml-5 space-y-1.5">
        {Array.isArray(data) ? 
          data.map((item, index) => renderItem(item, index)) :
          renderItem(data, 0)
        }
      </ul>
    </div>
  );
};

// Dynamic function to render all persona fields
const renderAllPersonaFields = (persona: any) => {
  // Fields to exclude from dynamic rendering (already shown in header or basic info)
  const excludedFields = ['id', 'name', 'created_at', 'updated_at', 'segment_id', '4_word_description', 'four_word_description'];
  
  // Basic info fields (simple strings/numbers)
  const basicInfoFields = ['age', 'gender', 'job_title', 'company_name', 'income', 'education', 'location'];
  
  // Professional fields
  const professionalFields = ['industry_l1', 'sub_industry_l2', 'function', 'role'];
  
  // Create a flattened persona object that includes data from the 'data' field if it exists
  const flattenedPersona = { ...persona };
  if (persona.data && typeof persona.data === 'object') {
    Object.entries(persona.data).forEach(([key, value]) => {
      // Never override 'id' from data - the top-level persona.id is always correct
      if (key !== 'id' && !flattenedPersona[key]) {
        flattenedPersona[key] = value;
      }
    });
  }
  
  // All other fields (arrays and objects)
  const dynamicFields: { [key: string]: any } = {};
  
  Object.entries(flattenedPersona).forEach(([key, value]) => {
    if (!excludedFields.includes(key) && 
        !basicInfoFields.includes(key) && 
        !professionalFields.includes(key) &&
        key !== 'data' && // Exclude the data field itself since we've flattened it
        value !== null && 
        value !== undefined && 
        value !== '') {
      dynamicFields[key] = value;
    }
  });

  // Color schemes for different sections
  const colorSchemes = [
    { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-800' },
    { bg: 'bg-rose-50', border: 'border-rose-100', text: 'text-rose-800' },
    { bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-800' },
    { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-800' },
    { bg: 'bg-green-50', border: 'border-green-100', text: 'text-green-800' },
    { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-800' },
    { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-800' },
    { bg: 'bg-pink-50', border: 'border-pink-100', text: 'text-pink-800' },
    { bg: 'bg-cyan-50', border: 'border-cyan-100', text: 'text-cyan-800' },
    { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-800' },
  ];

  const basicInfo: { [key: string]: any } = {};
  const professionalInfo: { [key: string]: any } = {};

  // Collect basic info
  basicInfoFields.forEach(field => {
    if (flattenedPersona[field] && flattenedPersona[field] !== '') {
      basicInfo[field] = flattenedPersona[field];
    }
  });

  // Collect professional info
  professionalFields.forEach(field => {
    if (flattenedPersona[field] && flattenedPersona[field] !== '') {
      professionalInfo[field] = flattenedPersona[field];
    }
  });

  // Helper function to calculate text length for sorting
  const getTextLength = (value: any): number => {
    if (typeof value === 'string') return value.length;
    if (Array.isArray(value)) {
      return value.reduce((total: number, item: any) => total + getTextLength(item), 0);
    }
    if (typeof value === 'object' && value !== null) {
      return Object.values(value).reduce((total: number, item: any) => total + getTextLength(item), 0);
    }
    return String(value).length;
  };

  // Prepare all sections with their content length for sorting
  const allSections = [];

  // Add basic info section if it has content
  if (Object.keys(basicInfo).length > 0) {
    const basicInfoLength = Object.values(basicInfo).reduce((total: number, value: any) => total + getTextLength(value), 0);
    allSections.push({
      type: 'basic',
      length: basicInfoLength,
      content: (
        <div className="bg-blue-50/50 p-5 rounded-lg border border-blue-100 h-fit">
          <h4 className="font-medium text-blue-800 mb-4 flex items-center">
            <User className="w-4 h-4 mr-2" />
            Personal Information
          </h4>
          <div className="space-y-3">
            {Object.entries(basicInfo).map(([key, value]) => (
              <div key={key} className="flex items-start">
                <span className="text-sm font-medium text-gray-600 w-28 capitalize">
                  {key.replace(/_/g, ' ')}:
                </span>
                <span className="text-sm text-gray-800">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )
    });
  }

  // Add professional info section if it has content
  if (Object.keys(professionalInfo).length > 0) {
    const professionalInfoLength = Object.values(professionalInfo).reduce((total: number, value: any) => total + getTextLength(value), 0);
    allSections.push({
      type: 'professional',
      length: professionalInfoLength,
      content: (
        <div className="bg-indigo-50/50 p-5 rounded-lg border border-indigo-100 h-fit">
          <h4 className="font-medium text-indigo-800 mb-4 flex items-center">
            <Building className="w-4 h-4 mr-2" />
            Professional Details
          </h4>
          <div className="space-y-3">
            {Object.entries(professionalInfo).map(([key, value]) => (
              <div key={key} className="flex items-start">
                <span className="text-sm font-medium text-gray-600 w-28 capitalize">
                  {key.replace(/_/g, ' ')}:
                </span>
                <span className="text-sm text-gray-800">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )
    });
  }

  // Add dynamic fields sections
  Object.entries(dynamicFields).forEach(([key, value], index) => {
    const colorScheme = colorSchemes[index % colorSchemes.length];
    const formattedTitle = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const textLength = getTextLength(value);
    
    allSections.push({
      type: 'dynamic',
      length: textLength,
      content: (
        <div key={key} className="h-fit">
          {renderPersonaData(value, formattedTitle, colorScheme.bg, colorScheme.border, colorScheme.text)}
        </div>
      )
    });
  });

  // Sort all sections by text length (ascending order)
  allSections.sort((a, b) => a.length - b.length);

  return (
    <div className="space-y-6">
      {/* Render all sections in a masonry-like grid, sorted by text length */}
      {allSections.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          {allSections.map((section, index) => (
            <div key={index}>
              {section.content}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Recursive JSON tree view
const JsonTree: React.FC<{ data: any; level?: number }> = ({ data, level = 0 }) => {
  const [open, setOpen] = useState(true);
  if (typeof data !== 'object' || data === null) {
    return <span className="text-blue-700">{JSON.stringify(data)}</span>;
  }
  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((v: any, i: number) => [i, v]) : Object.entries(data);
  return (
    <div className={`pl-${level * 4} border-l border-gray-100 ml-1`}> {/* Indent */}
      <button
        className="text-xs text-blue-500 hover:underline focus:outline-none mb-1"
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        {open ? '▼' : '▶'} {isArray ? 'Array' : 'Object'} ({entries.length})
      </button>
      {open && (
        <div className="ml-2">
          {entries.map(([k, v]: any) => (
            <div key={k} className="mb-0.5">
              <span className="text-gray-700 font-mono text-xs">
                {isArray ? '' : <span className="text-purple-700">{k}</span>}
                {isArray ? '' : ': '}
              </span>
              {typeof v === 'object' && v !== null ? (
                <JsonTree data={v} level={level + 1} />
              ) : (
                <span className="text-blue-700 font-mono text-xs">{JSON.stringify(v)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChatUI: React.FC<ChatUIProps> = React.memo(({ personaIds }) => {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<number[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDetailsId, setShowDetailsId] = useState<number | null>(null);
  const [details, setDetails] = useState<Persona | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [chatHistoryId, setChatHistoryId] = useState<number | null>(null);
  const [loadingPersonasCount, setLoadingPersonasCount] = useState(0);
  const [consolidatedPersonasJson, setConsolidatedPersonasJson] = useState<any>(null);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Media upload states
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [messageMediaMap, setMessageMediaMap] = useState<Record<string, {images?: string[], videos?: string[]}>>({});
  
  // Media context tracking for conversation history
  const [mediaContext, setMediaContext] = useState<string[]>([]);
  
  // Media viewer modal states
  const [viewerMedia, setViewerMedia] = useState<{type: 'image' | 'video', src: string} | null>(null);
  
  // Upload progress state
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  
  // Tooltip state
  const [showUploadTooltip, setShowUploadTooltip] = useState(false);
  

  // WebSocket chat functionality with isolation
  // Only enable chat when personas are selected
  const { 
    chatState, 
    sendMessage: sendChatMessage, 
    clearChat,
    isConnected: isChatConnected 
  } = useChatSocket({
    autoConnect: selectedPersonaIds.length > 0, // Only connect when personas are selected
    chatType: 'group',
    personaIds: selectedPersonaIds.length > 0 ? selectedPersonaIds : undefined,
    onMessageReceived: (data) => {
    },
    onResponseStarted: (data) => {
    },
    onResponseCompleted: (data) => {
      // Update consolidated personas from response if available
      if (data.consolidated_personas_json) {
        setConsolidatedPersonasJson(data.consolidated_personas_json);
      }
      // Also check for consolidated_personas_json in the WebSocket response metadata
      if (data.metadata?.consolidated_personas_json) {
        setConsolidatedPersonasJson(data.metadata.consolidated_personas_json);
      }
      // Update media context with descriptions from the response
      // Check both top-level and metadata for media_descriptions
      const mediaDescriptions = data.media_descriptions || data.metadata?.media_descriptions;
      if (mediaDescriptions && Array.isArray(mediaDescriptions)) {
        setMediaContext(prev => {
          // Remove any [Pending] placeholders and add the real descriptions
          const filtered = prev.filter(ctx => !ctx.startsWith('[Pending]'));
          const updated = [...filtered, ...mediaDescriptions];
          return updated;
        });
      }
    },
    onResponseFailed: (data) => {
      console.error('Group chat response failed:', data);
    }
  });

  // Progressive persona loading
  useEffect(() => {
    setPersonas([]);
    setLoading(true);
    setLoadingPersonasCount(personaIds.length);
    setIsSelectAllMode(false); // Reset Select All mode when persona IDs change
    let isCancelled = false;

    if (personaIds.length === 0) {
      setLoading(false);
      setLoadingPersonasCount(0);
      return;
    }

    personaIds.forEach(id => {
      fetch(`${API_URL}/personas/${id}`, { 
        credentials: 'include',
        signal: AbortSignal.timeout(5 * 60 * 1000) // 5 minute timeout
      })
        .then(res => res.ok ? res.json() : { id, name: `Persona ${id}` })
        .then(data => {
          if (!isCancelled) {
            // Ensure the correct ID is used - if data.data contains an id, remove it to prevent conflicts
            const personaData = { ...data };
            if (personaData.data && typeof personaData.data === 'object' && personaData.data.id !== undefined) {
              // Remove id from data to prevent it from overriding the correct persona ID
              const { id: _, ...dataWithoutId } = personaData.data;
              personaData.data = dataWithoutId;
            }
            // Ensure top-level id is always the correct one from the API response or the requested id
            personaData.id = data.id || id;
            
            setPersonas(prev => {
              if (prev.some(p => p.id === personaData.id)) return prev;
              return [...prev, personaData]; // Store full persona data with correct ID
            });
            
            // Auto-select this persona if we're in Select All mode
            setSelectedPersonaIds(prevSelected => {
              if (isSelectAllMode && !prevSelected.includes(id)) {
                return [...prevSelected, id];
              }
              return prevSelected;
            });
            
            setLoadingPersonasCount(count => count - 1);
          }
        })
        .catch(() => {
          if (!isCancelled) {
            setPersonas(prev => {
              if (prev.some(p => p.id === id)) return prev;
              return [...prev, { id, name: `Persona ${id}` }]; // Fallback data
            });
            
            // Auto-select this persona if we're in Select All mode (even for fallback data)
            setSelectedPersonaIds(prevSelected => {
              if (isSelectAllMode && !prevSelected.includes(id)) {
                return [...prevSelected, id];
              }
              return prevSelected;
            });
            
            setLoadingPersonasCount(count => count - 1);
          }
        });
    });

    setLoading(false);

    return () => { isCancelled = true; };
  }, [personaIds]);

  // Handle completion of persona loading when in Select All mode
  useEffect(() => {
    // When all personas are loaded and we're in Select All mode, ensure all are selected
    if (loadingPersonasCount === 0 && isSelectAllMode && personas.length > 0) {
      const allPersonaIds = personas.map(p => p.id);
      const missingPersonaIds = allPersonaIds.filter(id => !selectedPersonaIds.includes(id));
      
      if (missingPersonaIds.length > 0) {
        setSelectedPersonaIds(allPersonaIds);
      }
    }
  }, [loadingPersonasCount, isSelectAllMode, personas.length, selectedPersonaIds]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatState.messages, chatState.isTyping]);
  
  // Clean up media preview URLs on unmount
  useEffect(() => {
    return () => {
      mediaPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);
  
  // Reset upload state when typing or consolidation starts
  useEffect(() => {
    if (chatState.isTyping || chatState.isSending || chatState.isConsolidating) {
      setIsUploadingMedia(false);
    }
  }, [chatState.isTyping, chatState.isConsolidating]);

  // Fetch full details for modal
  const handleShowDetails = useCallback(async (id: number) => {
    setShowDetailsId(id);
    setLoadingDetails(true);
    try {
      const res = await fetch(`${API_URL}/personas/${id}`, { credentials: 'include' });
      if (res.ok) {
        setDetails(await res.json());
      } else {
        setDetails(null);
      }
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const handlePersonaSelect = useCallback((id: number, checked: boolean) => {
    setSelectedPersonaIds(prev => {
      const newSelected = checked ? [...prev, id] : prev.filter(pid => pid !== id);
      
      // If user manually deselects a persona, exit Select All mode
      if (!checked && isSelectAllMode) {
        setIsSelectAllMode(false);
      }
      
      return newSelected;
    });
    // Reset consolidated personas JSON when selection changes
    setConsolidatedPersonasJson(null);
    // Clear chat when persona selection changes since it's a different conversation
    clearChat();
  }, [isSelectAllMode, clearChat]);

  // Media handling functions
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    const validFiles: File[] = [];
    const previewUrls: string[] = [];
    
    Array.from(files).forEach(file => {
      // Check file size (20MB limit)
      if (file.size > 20 * 1024 * 1024) {
        alert(`File ${file.name} exceeds 20MB limit`);
        return;
      }
      
      // Check file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        alert(`File ${file.name} is not a supported image or video format`);
        return;
      }
      
      // Limit to 4 files total
      if (mediaFiles.length + validFiles.length >= 4) {
        alert('Maximum 4 files allowed');
        return;
      }
      
      validFiles.push(file);
      previewUrls.push(URL.createObjectURL(file));
    });
    
    if (validFiles.length > 0) {
      setMediaFiles(prev => [...prev, ...validFiles]);
      setMediaPreviewUrls(prev => [...prev, ...previewUrls]);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [mediaFiles.length]);
  
  const removeMedia = useCallback((index: number) => {
    // Clean up preview URL
    URL.revokeObjectURL(mediaPreviewUrls[index]);
    
    // Remove from arrays
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, [mediaPreviewUrls]);
  
  // Additional memoized callbacks
  const handleCloseDetails = useCallback(() => {
    setShowDetailsId(null);
  }, []);
  
  const handleViewMedia = useCallback((type: 'image' | 'video', src: string) => {
    setViewerMedia({ type, src });
  }, []);
  
  const handleCloseViewerMedia = useCallback(() => {
    setViewerMedia(null);
  }, []);
  
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  
  const handleClearMedia = useCallback(() => {
    mediaPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    setMediaFiles([]);
    setMediaPreviewUrls([]);
    setIsUploadingMedia(false);
  }, [mediaPreviewUrls]);
  
  const handleSelectAll = useCallback(() => {
    const allPersonasLoaded = loadingPersonasCount === 0;
    const allCurrentlySelected = selectedPersonaIds.length === personas.length && personas.length > 0;
    
    if (allCurrentlySelected) {
      // Deselect all
      setSelectedPersonaIds([]);
      setIsSelectAllMode(false);
    } else {
      // Select all currently loaded personas
      const allPersonaIds = personas.map(p => p.id);
      setSelectedPersonaIds(allPersonaIds);
      
      // If not all are loaded yet, enter Select All mode
      if (!allPersonasLoaded) {
        setIsSelectAllMode(true);
      }
    }
    
    // Reset consolidated personas JSON when selection changes
    setConsolidatedPersonasJson(null);
    clearChat();
  }, [loadingPersonasCount, selectedPersonaIds.length, personas, clearChat]);
  
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSend = async () => {
    if ((!input.trim() && mediaFiles.length === 0) || selectedPersonaIds.length === 0 || chatState.isTyping || isUploadingMedia) return;

    try {
      const message = input.trim();
      setInput(''); // Clear input field immediately
      
      // Show uploading state if media files are present
      if (mediaFiles.length > 0) {
        setIsUploadingMedia(true);
      }
      
      // Prepare media data if files are attached
      let mediaData: any = {};
      if (mediaFiles.length > 0) {
        const images: string[] = [];
        const videos: string[] = [];
        
        // Convert files to base64
        for (const file of mediaFiles) {
          const base64Data = await convertFileToBase64(file);
          
          if (file.type.startsWith('image/')) {
            images.push(base64Data);
          } else if (file.type.startsWith('video/')) {
            videos.push(base64Data);
          }
        }
        
        if (images.length > 0) mediaData.images = images;
        if (videos.length > 0) mediaData.videos = videos;
      }

      // Clear media files IMMEDIATELY before sending (for better UX)
      const tempMediaFiles = [...mediaFiles];
      const tempPreviewUrls = [...mediaPreviewUrls];
      
      // Clear input area immediately
      setMediaFiles([]);
      setMediaPreviewUrls([]);
      
      // Clean up preview URLs
      tempPreviewUrls.forEach(url => URL.revokeObjectURL(url));
      
      // Add the user message with media to the chat immediately for better UX
      const userMessageId = `user-${Date.now()}`;
      const userMessage = {
        id: userMessageId,
        content: message || 'Please analyze the attached media',
        role: 'user',
        timestamp: new Date().toISOString()
      };
      
      // Store media for this message if any
      let updatedMediaContext = mediaContext;
      if (tempMediaFiles.length > 0) {
        setMessageMediaMap(prev => ({
          ...prev,
          [userMessageId]: {
            images: mediaData.images || [],
            videos: mediaData.videos || []
          }
        }));
        
        // Don't add temporary context for the FIRST message with media
        // The AI will see the actual media and generate a proper description
        // Only add context for subsequent messages
        if (mediaContext.length > 0) {
          const tempDescription = `[Pending] User shared ${tempMediaFiles.length} file(s): ${tempMediaFiles.map(f => f.type.startsWith('image/') ? 'image' : 'video').join(', ')}`;
          updatedMediaContext = [...mediaContext, tempDescription];
          setMediaContext(updatedMediaContext);
        }
      }
      
      // Add message to chat state immediately
      chatState.messages.push(userMessage);
      
      // Send message via WebSocket with group chat options
      const chatOptions = {
        personaIds: selectedPersonaIds,
        chatHistoryId: chatHistoryId,
        consolidatedPersonasJson: consolidatedPersonasJson,
        mediaContext: updatedMediaContext,
        ...mediaData
      };
      await sendChatMessage(message || 'Please analyze the attached media', 'group', chatOptions);
      
      // Don't reset upload state here - let it reset when typing or consolidation starts

    } catch (err) {
      console.error('Error sending group chat message:', err);
      // Restore message to input if send failed
      setInput(input);
      // Reset upload state on error
      setIsUploadingMedia(false);
    }
  };

  return (
    <div className="flex flex-col h-[500px]">
      {/* Header with back button */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
        <button
          onClick={() => navigate('/')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Go to home"
          type="button"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h2 className="text-lg font-semibold text-gray-800">Chat with Personas</h2>
      </div>
      
      <div className="flex flex-1 bg-white rounded-b-xl shadow border border-t-0 border-gray-200 overflow-hidden">
        {/* Sidebar for persona selection */}
        <div className="w-64 min-w-[180px] max-w-[260px] bg-white border-r border-gray-100 flex flex-col h-full">
          <div className="px-4 py-4 border-b border-gray-100 font-semibold text-gray-700 text-base flex items-center gap-2">
            Personas
          {loadingPersonasCount > 0 && (
            <span className="ml-2">
              <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </span>
          )}
          <span className="flex-1" />
          <button
            className="text-xs bg-blue-500 hover:bg-blue-600 text-white rounded px-3 py-1 font-semibold shadow-sm transition-colors disabled:opacity-60 relative"
            type="button"
            disabled={personas.length === 0}
            onClick={() => {
              const allPersonasLoaded = loadingPersonasCount === 0;
              const allCurrentlySelected = selectedPersonaIds.length === personas.length && personas.length > 0;
              const isInSelectAllMode = isSelectAllMode;
              
              if (allCurrentlySelected && allPersonasLoaded) {
                // Deselect all
                setSelectedPersonaIds([]);
                setIsSelectAllMode(false);
              } else {
                // Select all mode - select currently loaded personas and flag for auto-selection
                setSelectedPersonaIds(personas.map(p => p.id));
                setIsSelectAllMode(true);
              }
              
              // Reset consolidated personas JSON when selection changes
              setConsolidatedPersonasJson(null);
              // Clear chat when persona selection changes since it's a different conversation
              clearChat();
            }}
          >
            {(() => {
              const allPersonasLoaded = loadingPersonasCount === 0;
              const allCurrentlySelected = selectedPersonaIds.length === personas.length && personas.length > 0;
              
              if (allCurrentlySelected && allPersonasLoaded && !isSelectAllMode) {
                return 'Deselect All';
              } else if (isSelectAllMode && !allPersonasLoaded) {
                return (
                  <span className="flex items-center gap-1">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Selecting... ({selectedPersonaIds.length}/{personaIds.length})
                  </span>
                );
              } else if (allCurrentlySelected && allPersonasLoaded && isSelectAllMode) {
                return `All Selected (${selectedPersonaIds.length})`;
              } else {
                return 'Select All';
              }
            })()}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {loading && personas.length === 0 && (
            <div className="flex flex-col justify-center items-center h-full py-8 text-gray-400">
              <svg className="animate-spin h-8 w-8 text-blue-500 mb-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <div>Loading personas...</div>
            </div>
          )}
          {!loading && personas.length === 0 && (
            <div className="text-gray-400 text-sm">No personas found.</div>
          )}
          {personas.length > 0 && personas.map(p => {
            // Extract job title from persona data
            const jobTitle = p.personal_information?.job_title || p.category || '';
            const isSelected = selectedPersonaIds.includes(p.id);
            
            return (
              <div key={`${p.id}-${isSelected}`} className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm mb-2">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={e => handlePersonaSelect(p.id, e.target.checked)}
                  className="accent-blue-600 w-4 h-4 mr-2 flex-shrink-0"
                  id={`persona-checkbox-${p.id}`}
                />
                <label htmlFor={`persona-checkbox-${p.id}`} className="text-sm font-medium text-gray-800 cursor-pointer select-none flex-1 min-w-0">
                  <div className="truncate">{p.name}</div>
                  {jobTitle && (
                    <div className="text-xs text-gray-500 truncate mt-0.5">{jobTitle}</div>
                  )}
                </label>
                <button
                  onClick={() => handleShowDetails(p.id)}
                  className="ml-2 text-blue-500 hover:text-blue-700 focus:outline-none flex-shrink-0"
                  title="Show details"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" /></svg>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col h-full">
        {/* Status Notifications Area - Fixed at top */}
        {(isUploadingMedia || chatState.isConsolidating) && (
          <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <svg className="animate-spin h-5 w-5 text-blue-600 flex-shrink-0" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <div className="text-sm">
                {isUploadingMedia && (
                  <>
                    <div className="font-semibold mb-1">Uploading Media</div>
                    <div>Converting and sending your images and videos to the personas...</div>
                  </>
                )}
                {chatState.isConsolidating && (
                  <>
                    <div className="font-semibold mb-1">Analyzing {selectedPersonaIds.length} Personas</div>
                    <div>The first response may take a couple of minutes, but after that everything runs much faster.</div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Chat area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50" ref={chatContainerRef}>
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg className="h-10 w-10 mb-2 animate-spin" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
              <div>Loading personas...</div>
            </div>
          ) : chatState.error ? (
            <div className="text-red-500 text-sm flex items-center justify-center h-full">{chatState.error}</div>
          ) : chatState.messages.length === 0 && !chatState.isConsolidating ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg className="h-10 w-10 mb-2" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.77 9.77 0 01-4-.8l-4 1 1-3A7.952 7.952 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              {selectedPersonaIds.length === 0 && !loading && !loadingPersonasCount ? (
                <div className="text-center">
                  <div className="mb-2">Select personas to start chatting</div>
                  <div className="text-sm text-gray-400">Choose one or more personas from the left sidebar</div>
                </div>
              ) : (
                <div>No messages yet. Start the conversation!</div>
              )}
              {(loadingPersonasCount > 0 || loading) && (
                <div className="mt-4 flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-2 rounded-lg">
                  <svg className="animate-spin h-4 w-4 text-teal-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  <span className="text-sm font-medium">
                    Loading profiles{loadingPersonasCount > 0 ? ` (${personas.length}/${personaIds.length})` : ''}, please wait...
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {chatState.messages.map((msg, idx) => {
                const isUser = msg.role === 'user';
                const isAI = msg.role === 'assistant';
                const mediaData = messageMediaMap[msg.id || `msg-${idx}`];
                const isLastUserMessage = isUser && idx === chatState.messages.length - 1;
                
                return (
                  <div key={msg.id || idx} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} animate-fadeIn`}>
                    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} w-full`}>
                      <div className={`max-w-[80%] px-4 py-2 rounded-lg shadow-sm ${isUser ? 'bg-blue-500 text-white rounded-br-none' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'}`}>
                        <div className="mb-1 text-xs font-semibold">
                          {isUser ? 'You' : 'Personas'}
                        </div>
                      
                      {/* Display attached media if present - only for user messages */}
                      {isUser && mediaData && (mediaData.images?.length > 0 || mediaData.videos?.length > 0) && (
                        <div className="mb-2 flex flex-wrap gap-2">
                          {/* Images */}
                          {mediaData.images?.map((img, imgIdx) => (
                            <div key={`img-${imgIdx}`} className="relative cursor-pointer group">
                              <img 
                                src={img} 
                                alt={`Attached ${imgIdx + 1}`}
                                className="max-w-[200px] max-h-[150px] rounded border border-white/20 object-cover"
                                onClick={() => setViewerMedia({type: 'image', src: img})}
                              />
                              <div 
                                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded flex items-center justify-center pointer-events-none"
                              >
                                <svg className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </div>
                            </div>
                          ))}
                          
                          {/* Videos */}
                          {mediaData.videos?.map((vid, vidIdx) => (
                            <div key={`vid-${vidIdx}`} className="relative cursor-pointer group">
                              <video 
                                src={vid}
                                className="max-w-[200px] max-h-[150px] rounded border border-white/20 object-cover"
                                controls={false}
                                muted
                              />
                              <div 
                                className="absolute inset-0 bg-black bg-opacity-30 rounded flex items-center justify-center cursor-pointer"
                                onClick={() => setViewerMedia({type: 'video', src: vid})}
                              >
                                <div className="bg-white/90 rounded-full p-2 pointer-events-none">
                                  <svg className="h-6 w-6 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="whitespace-pre-line text-sm">
                        {isAI ? (
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Show sending indicator below the last user message */}
                  {isLastUserMessage && chatState.isSending && !chatState.isTyping && (
                      <div className="flex items-center gap-1.5 mt-1 mr-2 text-xs text-gray-500">
                        <svg className="animate-pulse h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 2L11 13" />
                          <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                        </svg>
                        <span>Sending...</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {chatState.isTyping && !chatState.isConsolidating && (
                <div className="flex justify-start animate-fadeIn">
                  <div className="max-w-[80%] px-4 py-2 rounded-lg shadow-sm bg-white border border-gray-200 text-gray-800 rounded-bl-none flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    <span className="text-xs text-gray-500">typing...</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-gray-100 bg-white">
          {/* Media preview area */}
          {mediaFiles.length > 0 && (
            <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600 font-medium">
                  Attached Media ({mediaFiles.length}/4)
                </span>
                <button
                  onClick={() => {
                    mediaPreviewUrls.forEach(url => URL.revokeObjectURL(url));
                    setMediaFiles([]);
                    setMediaPreviewUrls([]);
                  }}
                  className="text-xs text-red-500 hover:text-red-700"
                  type="button"
                >
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {mediaFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    {file.type.startsWith('image/') ? (
                      <div className="relative">
                        <img 
                          src={mediaPreviewUrls[index]} 
                          alt={file.name}
                          className="h-16 w-16 object-cover rounded border border-gray-200"
                        />
                        <Image className="absolute bottom-1 right-1 h-3 w-3 text-white bg-blue-500 rounded p-0.5" />
                      </div>
                    ) : (
                      <div className="relative">
                        <video 
                          src={mediaPreviewUrls[index]}
                          className="h-16 w-16 object-cover rounded border border-gray-200"
                          muted
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-black bg-opacity-50 rounded-full p-1">
                            <Video className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => removeMedia(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      type="button"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <div className="text-xs text-gray-500 mt-1 truncate max-w-[64px]" title={file.name}>
                      {file.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Input controls */}
          <div className="px-4 py-3 flex items-center gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {/* Upload button with custom tooltip */}
            <div className="relative">
              <button
                onClick={handleUploadClick}
                onMouseEnter={() => setShowUploadTooltip(true)}
                onMouseLeave={() => setShowUploadTooltip(false)}
                className={`p-2 rounded-lg border transition-colors flex items-center gap-1 ${
                  mediaFiles.length >= 4 
                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed' 
                    : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
                type="button"
                disabled={chatState.isTyping || chatState.isSending || chatState.isConsolidating || isUploadingMedia || mediaFiles.length >= 4}
              >
                {isUploadingMedia ? (
                  <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                ) : (
                  <Upload className={`h-5 w-5 ${mediaFiles.length >= 4 ? 'text-gray-400' : 'text-gray-600'}`} />
                )}
                {mediaFiles.length > 0 && !isUploadingMedia && (
                  <span className={`text-xs font-medium ${mediaFiles.length >= 4 ? 'text-gray-400' : 'text-gray-600'}`}>
                    {mediaFiles.length}/4
                  </span>
                )}
              </button>
              
              {/* Custom visible tooltip */}
              {showUploadTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg pointer-events-none">
                  {isUploadingMedia ? "Uploading..." :
                   mediaFiles.length >= 4 ? "Max 4 files" : 
                   "Upload images/videos (Max 20MB)"}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
            
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={chatState.isConsolidating ? "Please wait while personas are being analyzed..." : "Type your message..."}
              className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500"
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleSend(); }}
              disabled={chatState.isTyping || chatState.isSending || chatState.isConsolidating}
            />
            <div
              title={
                selectedPersonaIds.length === 0 ? "Select personas first" :
                chatState.isConsolidating ? "Please wait while personas are being analyzed" :
                chatState.isSending ? "Sending message..." :
                chatState.isTyping ? "AI is responding..." :
                isUploadingMedia ? "Uploading media..." :
                (!input.trim() && mediaFiles.length === 0) ? "Type a message or attach media" :
                mediaFiles.length > 0 ? `Send message with ${mediaFiles.length} file(s)` :
                "Send message to personas"
              }
            >
              <button
                onClick={handleSend}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow-sm transition-colors disabled:opacity-60"
                type="button"
                disabled={chatState.isTyping || chatState.isSending || chatState.isConsolidating || isUploadingMedia || (!input.trim() && mediaFiles.length === 0) || selectedPersonaIds.length === 0}
              >
                {isUploadingMedia ? 'Uploading...' : chatState.isConsolidating ? 'Analyzing...' : chatState.isSending ? 'Sending...' : chatState.isTyping ? 'Typing...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-[90vw] max-w-[1200px] max-h-[90vh] flex flex-col">
            {/* Sticky Close (X) button */}
            <div className="sticky top-0 z-10 flex justify-end bg-white rounded-t-xl">
              <button
                onClick={handleCloseDetails}
                className="m-2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 focus:outline-none shadow"
                type="button"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {loadingDetails ? (
                <div className="flex flex-col justify-center items-center h-full py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-gray-600">Loading persona details...</p>
                </div>
              ) : details ? (
                <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-fadeIn">
                  {/* Persona Header */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-200">
                    <h3 className="text-2xl font-semibold text-gray-800 flex items-center">
                      <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 border border-blue-200">
                        {(findName(details) || 'U').charAt(0)}
                      </span>
                      {findName(details) || 'Unknown'}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600 mt-3 ml-1">
                      <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="font-medium">
                        {details.personal_information?.job_title || details.job_title || 'No title available'}
                        {details.company_name && ` at ${details.company_name}`}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white p-6">
                    {/* Dynamic rendering of all persona fields */}
                    {renderAllPersonaFields(details)}
                  </div>
                </div>
              ) : (
                <div className="text-red-500">Failed to load details.</div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Media Viewer Modal */}
      {viewerMedia && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90">
          {/* Close button */}
          <button
            onClick={handleCloseViewerMedia}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            type="button"
            aria-label="Close viewer"
          >
            <X className="h-6 w-6" />
          </button>
          
          {/* Media content */}
          <div className="max-w-[90vw] max-h-[90vh] flex items-center justify-center">
            {viewerMedia.type === 'image' ? (
              <img 
                src={viewerMedia.src} 
                alt="Full size preview"
                className="max-w-full max-h-[90vh] object-contain rounded"
              />
            ) : (
              <video 
                src={viewerMedia.src}
                controls
                autoPlay
                className="max-w-full max-h-[90vh] rounded"
              />
            )}
          </div>
          
          {/* Click outside to close */}
          <div 
            className="absolute inset-0 -z-10"
            onClick={handleCloseViewerMedia}
          />
        </div>
      )}
      </div>
    </div>
  );
});

ChatUI.displayName = 'ChatUI';

export default ChatUI; 